/**
 * Which addresses a contractor may claim.
 *
 * Shared by the browser (so the builder can say "that one is reserved" before
 * anybody pays) and by the publish route (which is the one that decides). The
 * server never trusts the browser's copy — it runs the same check again.
 */

export const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/

/**
 * Names nobody may take.
 *
 * A contractor publishing at `login.` or `billing.` under our domain is a
 * phishing page wearing our name, and `www` or `api` would collide with
 * infrastructure. Ported from contractor.wiki, plus this product's own words.
 */
export const RESERVED = new Set([
  "www", "api", "admin", "login", "signin", "sign-in", "signup", "sign-up",
  "account", "accounts", "billing", "pay", "payment", "payments", "checkout",
  "secure", "support", "help", "mail", "email", "webmail", "dashboard", "app",
  "cdn", "static", "assets", "status", "blog", "docs", "test", "dev", "staging",
  "invoice", "invoices", "invoice-builder", "website-builder",
  "contractor", "contractors", "freecontractorservices", "fcs", "wiki",
  "contractorwiki", "contractor-wiki",
])

/** Turns a business name into a candidate address. */
export function slugify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50)
}

/**
 * Returns an error message, or null when the slug is usable.
 *
 * Message text, not a code: every caller shows it to a person, and a table of
 * codes mapped back to sentences in two places is how the two drift.
 */
export function slugProblem(slug) {
  const value = String(slug || "")
  if (!value) return "Choose an address for your site."
  if (!SLUG_RE.test(value)) {
    return "Use 3–50 letters, numbers or hyphens, starting and ending with a letter or number."
  }
  if (RESERVED.has(value)) return `"${value}" is reserved. Please pick another address.`
  return null
}
